<template>
	<div
		class="hdl-rating question"
		:class="{
			'highlight-required': highlightRequired
		}"
		@keydown="keydownHandler"
	>
		<div ref="rendered" v-html="question.renderedQuestion" />
		<div
			class="container"
			ref="container"
		>
			<div class="stars">
				<star-indicator
					:key="index"
					v-for="index of choices"
					:class="{
						'selected': index <= selectedChoice
					}"
					tabindex="0"
					@click.stop="selectChoice(index)"
					:selected="index <= selectedChoice"
					:data-index=index
					ref="starChoices"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedSurveyQuestionType, QuestionDisplayStyle, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import StarIndicator from '@/components/common/StarIndicator.vue';

@Component({
	emits: ['intentionalBlur']
})
export default class StarRating extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: string | null;

	highlightRequired: boolean = false; /* Visually accentuate the fact that this question is required */

	selectedChoice: number = -1;

	numberInputTimeout: number | null = null;
	accumulatedNumbers: string = '';

	mounted(): void {
		this.$el.__vue__ = this;

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			this.selectedChoice = (existingResponse && existingResponse.value)? existingResponse.value : -1;
		}

		this.accessorizeRendered();
	}

	unmounted(): void {
		if (this.numberInputTimeout !== null) {
			clearTimeout(this.numberInputTimeout);
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const requiredIndicator = document.createElement('div');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const choices = this.$refs.starChoices as Array<StarIndicator> | undefined;

		if (choices === undefined) {
			return true;
		}

		if (this.selectedChoice > -1 && this.selectedChoice < this.choices.length) {
			choices[this.selectedChoice].$el.focus();
		} else if (choices.length > 0) {
			choices[0].$el.focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedIndex = this.focusedDomChoiceIndex();

		if (event.key === 'ArrowRight') {
			event.stopPropagation();
			event.preventDefault();
			const currentIndex = focusedIndex;
			if (currentIndex !== null && currentIndex < this.choices.length - 1) {
				const nextChoice = (this.$refs.starChoices as any)[currentIndex + 1];

				if (nextChoice) {
					nextChoice.$el.focus();
				}
			}
		} else if (event.key == 'ArrowLeft') {
			event.stopPropagation();
			event.preventDefault();
			const currentIndex = focusedIndex;
			if (currentIndex !== null && currentIndex > 0) {
				const nextChoice = (this.$refs.starChoices as any)[currentIndex - 1];
				if (nextChoice) {
					nextChoice.$el.focus();
				}
			}
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			event.preventDefault();

			if (focusedIndex !== null) {
				this.selectChoice(focusedIndex);
			}

			if (event.key === 'Enter') {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			}
		} else if (event.key === 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();
			this.$emit('intentionalBlur', this.$el, this.question, 'up');
		} else if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();
			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}

		if (event.key >= '0' && event.key <= '9') {
			event.stopPropagation();
			event.preventDefault();

			this.accumulatedNumbers += event.key;

			if (this.accumulatedNumbers.length === 1) {
				// 99% of the time it will be a single digit, set it immediately but still listen
				//	for more numbers
				const parsed = parseInt(event.key);
				if (!isNaN(parsed) && parsed > 0 && parsed <= this.choices.length) {
					this.selectChoice(parsed-1);
				}
			}

			this.numberInputTimeout = setTimeout(() => {
				if (this.accumulatedNumbers.length === 1) {
					// It was already handled
					this.accumulatedNumbers = '';
					return;
				}
				let parsed = parseInt(this.accumulatedNumbers);
				this.accumulatedNumbers = '';

				if (isNaN(parsed)) {
					return;
				}

				parsed -= 1; // 0 base it

				if (parsed < 0 || parsed > this.choices.length - 1) {
					return;
				}

				this.selectChoice(parsed);
			}, 400);
		} else {
			this.accumulatedNumbers = '';
		}
	}

	selectChoice(selectedChoice: number): void {
		if (this.selectedChoice === selectedChoice) {
			this.selectedChoice = - 1;

			if (this.responseGroup) {
				this.responseGroup.setResponse(this.question.uuid, null, null, null);
			}
			return;
		}

		this.highlightRequired = false;

		this.selectedChoice = selectedChoice;

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, null, this.selectedChoice, null);
		}
	}

	getResponse(): number | null {
		return (this.selectedChoice === -1)? null : this.selectedChoice;
	}

	hasResponse(): boolean {
		return this.selectedChoice !== -1;
	}

	markError(error: SurveyResponseInputErrorType): void {
		if (error.code === SurveyResponseErrorCode.MISSING_REQUIRED) {
			this.highlightRequired = true;
		}
	}

	focusedDomChoiceIndex(): number | null {
		const container = this.$refs.container as HTMLDivElement | undefined;

		if (container === undefined) {
			return null;
		}

		if (document.activeElement && container.contains(document.activeElement)) {
			const index = document.activeElement.getAttribute('data-index');
			if (index === null) {
				return null;
			}

			const parsedIndex = parseInt(index);
			if (isNaN(parsedIndex)) {
				return null;
			}

			return parsedIndex;
		}

		return null;
	}

	get choices(): Array<number> {
		const end = this.question.limitMax ?? 5;

		const result: Array<number> = [];

		for (let i = 0; i < end; i += 1) {
			result.push(i);
		}

		return result;
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-rating {
	&.highlight-required {
		label {
			@apply text-hdl-red;
		}
	}

	.container {
		width: fit-content;

		.stars {
			@apply flex flex-row items-center flex-wrap gap-y-2;

			.star-indicator {
				@apply mr-2;
			}
		}
	}
}
</style>
